import { isDefined, isNonEmpty, isNotDefined, makeValuesPredicate } from '@sgme/fp';
import { BlotterType } from 'models/blotters/BlotterType';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import type { AppState } from 'state/state.model';
import { assert } from 'utils/assert/base';

import type { TradeCancellation, TradeCancellationStatus, TradeCancellationsState } from './cancellations.models';

function selectTradeCancellationsState(state: AppState): TradeCancellationsState {
  return state.trades.cancellations;
}

export function getTradeCancellationTradeId(appState: AppState): string | undefined {
  const state = selectTradeCancellationsState(appState);
  assert(Object.keys(state).length <= 1, 'Trade cancellation store should not more than contain 1 key');
  return Object.keys(state)[0] ?? undefined;
}

export function getTradeCancellation(appState: AppState, tradeId?: string): TradeCancellation | undefined {
  if (isNotDefined(tradeId)) {
    return undefined;
  }
  const state = selectTradeCancellationsState(appState);
  return state[tradeId];
}

export function getTradeCancellationFileCode(appState: AppState, tradeId?: string): string | undefined {
  const tradeCancellation = getTradeCancellation(appState, tradeId);
  return tradeCancellation?.blotterItem.fileCode;
}

export function getTradeCancellationExternalQuoteId(appState: AppState, tradeId?: string): string | undefined {
  const tradeCancellation = getTradeCancellation(appState, tradeId);
  return tradeCancellation?.blotterItem.externalQuoteId;
}

export function getIsFileCodeExist(appState: AppState, tradeId?: string): boolean | undefined {
  const fileCode = getTradeCancellationFileCode(appState, tradeId);
  return isDefined(fileCode) && isNonEmpty(fileCode);
}

export function getTradeCancellationStatus(appState: AppState, tradeId?: string): TradeCancellationStatus | undefined {
  const tradeCancellation = getTradeCancellation(appState, tradeId);
  return tradeCancellation?.status;
}
export function getTradeCancellationItemType(appState: AppState, tradeId?: string): BlotterType | undefined {
  return getTradeCancellation(appState, tradeId)?.blotterType;
}

export function getTradeCancellationErrors(appState: AppState, tradeId?: string): NextWorkflowError[] | undefined {
  const tradeCancellation = getTradeCancellation(appState, tradeId);
  return tradeCancellation?.error?.workflowErrors;
}

const isFailed = makeValuesPredicate(['FAILED', 'REJECTED']);

export function isTradeCancellationFailed(appState: AppState, tradeId?: string): boolean {
  const status = getTradeCancellationStatus(appState, tradeId);
  return isFailed(status);
}

export function isTradeCancellationSucceeded(appState: AppState, tradeId?: string): boolean {
  const status = getTradeCancellationStatus(appState, tradeId);
  return status ? status === 'SUCCESS' : false;
}

export function isTradeCancellationWorkflowInitiated(appState: AppState, tradeId?: string): boolean {
  const status = getTradeCancellationStatus(appState, tradeId);
  return status ? status === 'INIT' : false;
}

export function isTradeCancellationWorkflowActivated(
  appState: AppState,
  tradeCancellationStatus: TradeCancellationStatus,
  tradeId?: string,
): boolean {
  const status = getTradeCancellationStatus(appState, tradeId);
  return status ? status === tradeCancellationStatus : false;
}

export function getIconByStatus(appState: AppState): string {
  const tradeId = getTradeCancellationTradeId(appState);
  const status = getTradeCancellationStatus(appState, tradeId);
  const isFileCodeExist = getIsFileCodeExist(appState, tradeId);
  switch (status) {
    case 'SUCCESS':
      return isFileCodeExist ? 'info' : 'cancel';
    case 'IN_PROGRESS':
    case 'INIT':
      return 'priority_high';
    default:
      return 'info';
  }
}

export function getBorderColorClass(appState: AppState): string | undefined {
  const tradeId = getTradeCancellationTradeId(appState);
  const status = getTradeCancellationStatus(appState, tradeId);
  switch (status) {
    case 'SUCCESS':
      return undefined;
    default:
      return 'border-danger';
  }
}

export function getTranslationKeyForStatus(appState: AppState): string | undefined {
  const tradeId = getTradeCancellationTradeId(appState);
  const status = getTradeCancellationStatus(appState, tradeId);
  const isFileCodeExist = getIsFileCodeExist(appState, tradeId);
  switch (status) {
    case 'SUCCESS':
      return isFileCodeExist ? 'SUCCESS' : 'CANCELLED';
    case 'REJECTED':
      return 'FAILED';
    case 'IN_PROGRESS':
      return 'INIT';
    default:
      return status;
  }
}

export function getStatusColor(appState: AppState): string | undefined {
  const tradeId = getTradeCancellationTradeId(appState);
  const status = getTradeCancellationStatus(appState, tradeId);
  const isFileCodeExist = getIsFileCodeExist(appState, tradeId);
  switch (status) {
    case 'SUCCESS':
      return isFileCodeExist ? 'info' : 'black';
    default:
      return 'danger';
  }
}
